import { Alert, Button, Card, CardBody } from '@foundation/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { AppLayout } from '@/components/AppLayout'
import { api } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/settings-api')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: ApiKeysPage,
})

function ApiKeysPage() {
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [keyPrefix, setKeyPrefix] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isRevoking, setIsRevoking] = useState(false)

  const handleCreateKey = (): void => {
    setIsCreating(true)
    api
      .createApiKey()
      .then((result) => {
        setApiKey(result.apiKey)
        setKeyPrefix(result.keyPrefix)
        toast.success('API key created successfully')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to create API key')
      })
      .finally(() => setIsCreating(false))
  }

  const handleRevokeKey = (): void => {
    if (!keyPrefix) return

    setIsRevoking(true)
    api
      .revokeApiKey(keyPrefix)
      .then(() => {
        setApiKey(null)
        setKeyPrefix(null)
        toast.success('API key revoked successfully')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to revoke API key')
      })
      .finally(() => setIsRevoking(false))
  }

  return (
    <AppLayout title="API Keys">
      <Card>
        <CardBody>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>API Keys</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            Manage API keys for programmatic access. Available on Business plan only.
          </p>

          {apiKey && (
            <Alert variant="success" style={{ marginBottom: '1rem' }}>
              Your new API key: <code style={{ fontFamily: 'var(--font-mono)' }}>{apiKey}</code>
              <br />
              <strong>Copy this key now - it won&apos;t be shown again.</strong>
            </Alert>
          )}

          {keyPrefix && !apiKey && (
            <div
              style={{
                padding: '1rem',
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Active key: <code>{keyPrefix}...</code>
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <Button variant="primary" onClick={handleCreateKey} loading={isCreating}>
              Generate New API Key
            </Button>

            {keyPrefix && (
              <Button variant="danger" onClick={handleRevokeKey} loading={isRevoking}>
                Revoke Key
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </AppLayout>
  )
}
